import { FolderKanban } from 'lucide-react'
import { BottomSheet } from '@/components/BottomSheet'
import type { SpaceItem } from '@/lib/api-types'
import type { ContextMode } from '@/pages/chat/chat-types'

type ChatSettingsSheetProps = {
  open: boolean
  spaces: SpaceItem[]
  selectedSpaceId?: string
  contextMode: ContextMode
  onClose: () => void
  onSelectSpace: (spaceId: string) => void
  onApplyContextMode: (mode: ContextMode) => void
  currentChatTitle: string
  canManageCurrentChat: boolean
  onRenameCurrentChat: () => void
  onDeleteCurrentChat: () => void
  theme: 'light' | 'dark'
  onSetTheme: (theme: 'light' | 'dark') => void
  onOpenProjects: () => void
}

export function ChatSettingsSheet({
  open,
  spaces,
  selectedSpaceId,
  contextMode,
  onClose,
  onSelectSpace,
  onApplyContextMode,
  currentChatTitle,
  canManageCurrentChat,
  onRenameCurrentChat,
  onDeleteCurrentChat,
  theme,
  onSetTheme,
  onOpenProjects,
}: ChatSettingsSheetProps) {
  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Настройки этого чата"
      description="Выберите проект для текущего чата и режим контекста."
      maxBodyHeight="74vh"
    >
      <div className="flex flex-col gap-2 pb-1">
        <div className="glass-surface" style={{ padding: '8px 10px' }}>
          <p className="text-[11px] font-semibold truncate">{currentChatTitle}</p>
          <div className="mt-1.5 flex items-center gap-1.5">
            <button
              type="button"
              onClick={onRenameCurrentChat}
              disabled={!canManageCurrentChat}
              className="rounded-lg px-2 py-1 text-[10px] glass-icon active:scale-95 transition-transform disabled:opacity-45"
            >
              Переименовать чат
            </button>
            <button
              type="button"
              onClick={onDeleteCurrentChat}
              disabled={!canManageCurrentChat}
              className="rounded-lg px-2 py-1 text-[10px] glass-icon text-red-500/90 active:scale-95 transition-transform disabled:opacity-45"
            >
              Удалить чат
            </button>
          </div>
        </div>

        <div className="glass-surface" style={{ padding: '8px 10px' }}>
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-[11px] font-semibold inline-flex items-center gap-1">
              <FolderKanban size={12} className="text-telegram-button" />
              Проект чата
            </p>
            <button
              type="button"
              onClick={onOpenProjects}
              className="text-[10px] text-telegram-button active:scale-95 transition-transform"
            >
              Создать новый проект
            </button>
          </div>
          <div className="max-h-[160px] overflow-y-auto flex flex-col gap-1">
            {spaces.length === 0 ? (
              <p className="text-[11px] text-telegram-hint">Проекты не найдены</p>
            ) : (
              spaces.map((space) => {
                const active = space.id === selectedSpaceId
                return (
                  <button
                    key={space.id}
                    type="button"
                    onClick={() => onSelectSpace(space.id)}
                    className={`w-full text-left rounded-lg px-2 py-1.5 transition-all active:scale-[0.99] ${
                      active ? 'bg-telegram-button text-white' : 'glass-icon text-telegram-text'
                    }`}
                  >
                    <p className="text-[11px] font-semibold truncate">
                      {space.name}
                      {space.is_personal ? ' (Личный)' : ''}
                    </p>
                    {space.description && (
                      <p className={`text-[10px] truncate mt-0.5 ${active ? 'text-white/85' : 'text-telegram-hint'}`}>
                        {space.description}
                      </p>
                    )}
                  </button>
                )
              })
            )}
          </div>
        </div>

        <div className="glass-surface" style={{ padding: '8px 10px' }}>
          <p className="text-[11px] font-semibold mb-1.5">Режим контекста</p>
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={() => onApplyContextMode('isolated')}
              className={`w-full text-left rounded-lg px-2.5 py-2 transition-all active:scale-[0.99] ${
                contextMode === 'isolated'
                  ? 'bg-telegram-button text-white'
                  : 'glass-icon text-telegram-text'
              }`}
            >
              <p className="text-[11px] font-semibold">Личный</p>
              <p className={`text-[10px] mt-0.5 ${contextMode === 'isolated' ? 'text-white/85' : 'text-telegram-hint'}`}>
                Только текущий диалог и прикреплённые документы.
              </p>
            </button>
            <button
              type="button"
              onClick={() => onApplyContextMode('space_shared')}
              className={`w-full text-left rounded-lg px-2.5 py-2 transition-all active:scale-[0.99] ${
                contextMode === 'space_shared'
                  ? 'bg-telegram-button text-white'
                  : 'glass-icon text-telegram-text'
              }`}
            >
              <p className="text-[11px] font-semibold">Общий по проекту</p>
              <p className={`text-[10px] mt-0.5 ${contextMode === 'space_shared' ? 'text-white/85' : 'text-telegram-hint'}`}>
                Учитывает историю чатов выбранного проекта.
              </p>
            </button>
          </div>
        </div>

        <div className="glass-surface" style={{ padding: '8px 10px' }}>
          <p className="text-[11px] font-semibold mb-1.5">Тема оформления</p>
          <div className="grid grid-cols-2 gap-1.5">
            <button
              type="button"
              onClick={() => onSetTheme('light')}
              className={`rounded-lg px-2 py-1.5 text-[10px] transition-all active:scale-[0.99] ${
                theme === 'light' ? 'bg-telegram-button text-white' : 'glass-icon text-telegram-text'
              }`}
            >
              Светлая
            </button>
            <button
              type="button"
              onClick={() => onSetTheme('dark')}
              className={`rounded-lg px-2 py-1.5 text-[10px] transition-all active:scale-[0.99] ${
                theme === 'dark' ? 'bg-telegram-button text-white' : 'glass-icon text-telegram-text'
              }`}
            >
              Тёмная
            </button>
          </div>
        </div>
      </div>
    </BottomSheet>
  )
}
